/**
 * Footer chuẩn dialog form custom: Hủy + Submit, busy spinner/nhãn, optional hint trái.
 * Pair với `blockDialogCloseWhenBusy` trên Dialog `onClose`.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import Button, { type ButtonIntent } from '../actions/Button';
import BusySubmitButton from '../actions/BusySubmitButton';
import Box from '../primitives/Box';
import { mergeSx } from '../utils/mergeSx';
import { DialogActions, type DialogActionsProps } from './Dialog';

export interface FormDialogFooterProps extends Omit<DialogActionsProps, 'children'> {
  busy?: boolean;
  onCancel: () => void;
  onSubmit?: () => void;
  cancelLabel?: string;
  /** Disables Hủy without showing the submit spinner. Defaults to `busy`. */
  cancelDisabled?: boolean;
  submitLabel: string;
  busySubmitLabel: string;
  submitDisabled?: boolean;
  submitIntent?: ButtonIntent;
  /** Overrides the outlined neutral cancel button. */
  cancelVariant?: 'text' | 'outlined';
  submitType?: 'button' | 'submit';
  hideSubmit?: boolean;
  hideCancel?: boolean;
  /** Left-aligned hint (e.g. required-fields note). */
  startSlot?: React.ReactNode;
  /** Extra buttons between Cancel and Submit. */
  between?: React.ReactNode;
}

const FormDialogFooter: React.FC<FormDialogFooterProps> = ({
  busy = false,
  onCancel,
  onSubmit,
  cancelLabel,
  cancelDisabled,
  submitLabel,
  busySubmitLabel,
  submitDisabled = false,
  submitIntent = 'primary',
  cancelVariant,
  submitType = 'button',
  hideSubmit = false,
  hideCancel = false,
  startSlot,
  between,
  sx,
  ...dialogActionsProps
}) => {
  const { t } = useTranslation('common');

  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {!hideCancel ? (
        <Button intent="neutral" variant={cancelVariant} onClick={onCancel} disabled={cancelDisabled ?? busy}>
          {cancelLabel ?? t('cancel')}
        </Button>
      ) : null}
      {between}
      {!hideSubmit && onSubmit ? (
        <BusySubmitButton
          intent={submitIntent}
          type={submitType}
          busy={busy}
          label={submitLabel}
          busyLabel={busySubmitLabel}
          disabled={submitDisabled}
          onClick={onSubmit}
        />
      ) : null}
    </Box>
  );

  return (
    <DialogActions
      sx={mergeSx(
        {
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
          ...(startSlot ? { justifyContent: 'space-between' } : {}),
        },
        sx,
      )}
      {...dialogActionsProps}
    >
      {startSlot ?? null}
      {actionButtons}
    </DialogActions>
  );
};

export default FormDialogFooter;
